<template>
  <div class="user-form-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="info in infoList"
          :key="info.id"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-user-cog me-2 text-primary"></i>
            <strong>{{ obj.id ? 'Edit' : 'Add' }} User</strong>
          </h4>
          <small class="text-muted">{{ obj.id ? 'Update existing user account' : 'Create a new user account' }}</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading user data...</p>
      </div>
    </div>

    <!-- User Form with Tabs -->
    <CRow v-else>
      <CCol sm="12">
        <CTabs add-tab-classes="mt-1">
          <!-- Tab 1: System Access -->
          <CTab active>
            <template slot="title">
              <i class="fas fa-user-lock"></i> System Access
            </template>
            <CCard class="form-card">
              <CCardHeader class="form-header">
                <div class="d-flex align-items-center">
                  <i class="fas fa-user-lock me-2 text-primary"></i>
                  <strong>System Access & Security</strong>
                </div>
              </CCardHeader>
              <CCardBody>
                <CForm>
                  <!-- Email -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Email</strong>
                      <span class="text-danger ms-1">*</span>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="$v.obj.email.$model"
                        :isValid="checkIfValid('email')"
                        invalidFeedback="Valid email address is required"
                        placeholder="user@example.com"
                        type="email"
                        :readonly="isEmailReadOnly"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-envelope me-1"></i>
                        Used for login and notifications
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Role -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Role</strong>
                      <span class="text-danger ms-1">*</span>
                    </CCol>
                    <CCol sm="9">
                      <CSelect
                        :options="roles"
                        :value.sync="selectedRoleId"
                        @change="onRoleSelected()"
                        placeholder="Select user role"
                        :disabled="isReadOnly"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-user-tag me-1"></i>
                        User role determines access permissions
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Account Status -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Account Status</strong>
                    </CCol>
                    <CCol sm="9">
                      <div class="d-flex align-items-center">
                        <CSwitch
                          color="success"
                          :checked.sync="obj.isEnable"
                        />
                        <span class="ms-3">
                          <CBadge :color="obj.isEnable ? 'success' : 'secondary'">
                            {{ obj.isEnable ? 'Enabled' : 'Disabled' }}
                          </CBadge>
                          <small class="ms-2 text-muted">
                            {{ obj.isEnable ? 'User can login and access the system' : 'User cannot login' }}
                          </small>
                        </span>
                      </div>
                    </CCol>
                  </CRow>

                  <!-- Divider -->
                  <hr class="my-4" />

                  <!-- Password Section Header -->
                  <CRow class="mb-3">
                    <CCol>
                      <h6 class="section-header">
                        <i class="fas fa-key me-2 text-primary"></i>
                        <strong>Password Management</strong>
                      </h6>
                    </CCol>
                  </CRow>

                  <!-- Reset Password Toggle -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Reset Password?</strong>
                    </CCol>
                    <CCol sm="9">
                      <div class="d-flex align-items-center">
                        <CSwitch
                          color="primary"
                          :checked.sync="isResetPassword"
                        />
                        <span class="ms-3 text-muted">
                          <small v-if="isResetPassword">
                            <i class="fas fa-check-circle text-success me-1"></i>
                            Password reset enabled
                          </small>
                          <small v-else>
                            <i class="fas fa-times-circle text-muted me-1"></i>
                            Password reset disabled
                          </small>
                        </span>
                      </div>
                    </CCol>
                  </CRow>

                  <!-- Password Field (conditional) -->
                  <CRow v-if="isResetPassword" class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Password</strong>
                      <span class="text-danger ms-1">*</span>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="plainPassword"
                        autocomplete="new-password"
                        type="password"
                        placeholder="Enter new password"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-lock me-1"></i>
                        Minimum 8 characters with combination of uppercase, lowercase, numbers and special characters
                      </small>
                    </CCol>
                  </CRow>
                </CForm>
              </CCardBody>
              <CCardFooter class="form-footer">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <div class="mb-2 mb-sm-0">
                    <CButton
                      v-if="canSubmit"
                      color="primary"
                      :disabled="!isValid || submitting"
                      @click="onSubmit"
                    >
                      <CSpinner v-if="submitting" size="sm" class="me-2" />
                      <i v-else class="fas fa-check-circle me-2"></i>
                      {{ submitting ? 'Saving...' : 'Submit' }}
                    </CButton>
                    <CButton
                      color="warning"
                      variant="outline"
                      class="ms-2"
                      :disabled="isValid"
                      @click="validate"
                    >
                      <i class="fas fa-exclamation-triangle me-2"></i>
                      Validate
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      class="ms-2"
                      :disabled="!isDirty"
                      @click="reset"
                    >
                      <i class="fas fa-undo me-2"></i>
                      Reset
                    </CButton>
                    <CButton
                      color="secondary"
                      class="ms-2"
                      @click="cancel"
                      :disabled="submitting"
                    >
                      <i class="fas fa-times me-2"></i>
                      Cancel
                    </CButton>
                  </div>
                  <div v-if="!isValid" class="text-danger">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    <small>Please fill in all required fields correctly</small>
                  </div>
                </div>
              </CCardFooter>
            </CCard>
          </CTab>

          <!-- Tab 2: Personal & Company Info -->
          <CTab>
            <template slot="title">
              <i class="fas fa-id-card"></i> Personal & Company Info
            </template>
            <CCard class="form-card">
              <CCardHeader class="form-header">
                <div class="d-flex align-items-center">
                  <i class="fas fa-id-card me-2 text-primary"></i>
                  <strong>Personal & Company Information</strong>
                </div>
              </CCardHeader>
              <CCardBody>
                <CForm>
                  <!-- Personal Information Section Header -->
                  <CRow class="mb-3">
                    <CCol>
                      <h6 class="section-header">
                        <i class="fas fa-user me-2 text-primary"></i>
                        <strong>Personal Information</strong>
                      </h6>
                    </CCol>
                  </CRow>

                  <!-- Full Name -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Full Name</strong>
                      <span class="text-danger ms-1">*</span>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="$v.obj.fullName.$model"
                        :isValid="checkIfValid('fullName')"
                        :readonly="isReadOnly"
                        invalidFeedback="Full name is required (minimum 3 characters)"
                        placeholder="Enter full name"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-info-circle me-1"></i>
                        Full legal name of the user
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Phone -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Phone</strong>
                      <span class="text-danger ms-1">*</span>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="$v.obj.phone.$model"
                        :isValid="checkIfValid('phone')"
                        :readonly="isReadOnly"
                        invalidFeedback="Valid phone number is required (8-11 digits)"
                        placeholder="Enter phone number"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-phone me-1"></i>
                        Contact number (8-11 digits, numbers only)
                      </small>
                    </CCol>
                  </CRow>

                  <!-- MyKad -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>MyKad</strong>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="obj.myKad"
                        :readonly="isReadOnly"
                        placeholder="Enter MyKad number"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-id-card me-1"></i>
                        Malaysian identity card number
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Passport Number -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Passport Number</strong>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="obj.passportNo"
                        :readonly="isReadOnly"
                        placeholder="Enter passport number"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-passport me-1"></i>
                        International passport number
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Divider -->
                  <hr class="my-4" />

                  <!-- Company Information Section Header -->
                  <CRow class="mb-3">
                    <CCol>
                      <h6 class="section-header">
                        <i class="fas fa-building me-2 text-primary"></i>
                        <strong>Company Information</strong>
                      </h6>
                    </CCol>
                  </CRow>

                  <!-- Sub Company -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Sub Company</strong>
                    </CCol>
                    <CCol sm="9">
                      <CSelect
                        :options="subCompanies"
                        :value.sync="obj.subCompanyId"
                        placeholder="Select sub company"
                        :disabled="isReadOnly"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-sitemap me-1"></i>
                        Sub company/branch association
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Bank Account -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Bank Account</strong>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="obj.bankAccount"
                        :readonly="isReadOnly"
                        placeholder="Enter bank account number"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-university me-1"></i>
                        Personal bank account number
                      </small>
                    </CCol>
                  </CRow>

                  <!-- TIN Number -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>TIN Number</strong>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="obj.tinNo"
                        :readonly="isReadOnly"
                        placeholder="Enter TIN number"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-file-invoice me-1"></i>
                        Tax Identification Number
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Company Name -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Company Name</strong>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="obj.companyName"
                        :readonly="isReadOnly"
                        placeholder="Enter company name"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-briefcase me-1"></i>
                        Registered company name
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Company Bank Account -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Company Bank Account</strong>
                    </CCol>
                    <CCol sm="9">
                      <CInput
                        v-model="obj.companyBankAccount"
                        :readonly="isReadOnly"
                        placeholder="Enter company bank account number"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-money-check me-1"></i>
                        Company bank account number
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Address -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Address</strong>
                    </CCol>
                    <CCol sm="9">
                      <CTextarea
                        v-model="obj.address"
                        :readonly="isReadOnly"
                        placeholder="Enter address"
                        rows="3"
                      />
                      <small class="form-text text-muted">
                        <i class="fas fa-map-marker-alt me-1"></i>
                        Full address
                      </small>
                    </CCol>
                  </CRow>

                  <!-- Divider -->
                  <hr class="my-4" />

                  <!-- Documents Section Header -->
                  <CRow class="mb-3">
                    <CCol>
                      <h6 class="section-header">
                        <i class="fas fa-file-upload me-2 text-primary"></i>
                        <strong>Documents</strong>
                      </h6>
                    </CCol>
                  </CRow>

                  <!-- IC Copy Upload -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>IC Copy</strong>
                    </CCol>
                    <CCol sm="9">
                      <!-- Upload Component -->
                      <div v-if="obj.id" class="mb-3">
                        <WidgetsUploadDocument
                          @uploaded="icCopy_uploaded"
                          :acceptedFileTypes="['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']"
                          :maxFileSize="5"
                          label="Upload IC Copy"
                        />
                      </div>
                      <div v-else class="alert alert-info">
                        <i class="fas fa-info-circle me-2"></i>
                        Please save the user first before uploading documents
                      </div>

                      <!-- Thumbnail Display -->
                      <div v-if="obj.icCopyDocument" class="document-preview">
                        <div class="preview-card">
                          <div class="preview-header">
                            <strong>
                              <i class="fas fa-file me-2"></i>
                              IC Copy Document
                            </strong>
                            <CButton
                              color="danger"
                              size="sm"
                              variant="ghost"
                              @click="deleteIcCopy"
                              title="Delete IC Copy"
                            >
                              <i class="fas fa-trash"></i>
                            </CButton>
                          </div>
                          <div class="preview-body">
                            <!-- Display PDF inline with iframe -->
                            <iframe
                              v-if="!isImageDocument(obj.icCopyDocument)"
                              :src="getDocumentUrl(obj.icCopyDocument)"
                              width="100%"
                              height="600px"
                              class="document-iframe"
                            ></iframe>
                            <!-- Display Image inline -->
                            <img
                              v-else
                              :src="getDocumentUrl(obj.icCopyDocument)"
                              alt="IC Copy"
                              class="document-image-inline"
                            />
                          </div>
                        </div>
                      </div>
                    </CCol>
                  </CRow>

                  <!-- Photo/ID Upload -->
                  <CRow class="mb-3">
                    <CCol tag="label" sm="3" class="col-form-label">
                      <strong>Photo/ID</strong>
                    </CCol>
                    <CCol sm="9">
                      <!-- Upload Component -->
                      <div v-if="obj.id" class="mb-3">
                        <WidgetsUploadDocument
                          @uploaded="photo_uploaded"
                          :acceptedFileTypes="['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']"
                          :maxFileSize="5"
                          label="Upload Photo/ID"
                        />
                      </div>
                      <div v-else class="alert alert-info">
                        <i class="fas fa-info-circle me-2"></i>
                        Please save the user first before uploading documents
                      </div>

                      <!-- Thumbnail Display -->
                      <div v-if="obj.photoDocument" class="document-preview">
                        <div class="preview-card">
                          <div class="preview-header">
                            <strong>
                              <i class="fas fa-file me-2"></i>
                              Photo/ID Document
                            </strong>
                            <CButton
                              color="danger"
                              size="sm"
                              variant="ghost"
                              @click="deletePhoto"
                              title="Delete Photo"
                            >
                              <i class="fas fa-trash"></i>
                            </CButton>
                          </div>
                          <div class="preview-body">
                            <!-- Display PDF inline with iframe -->
                            <iframe
                              v-if="!isImageDocument(obj.photoDocument)"
                              :src="getDocumentUrl(obj.photoDocument)"
                              width="100%"
                              height="600px"
                              class="document-iframe"
                            ></iframe>
                            <!-- Display Image inline -->
                            <img
                              v-else
                              :src="getDocumentUrl(obj.photoDocument)"
                              alt="Photo/ID"
                              class="document-image-inline"
                            />
                          </div>
                        </div>
                      </div>
                    </CCol>
                  </CRow>
                </CForm>
              </CCardBody>
              <CCardFooter class="form-footer">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <div class="mb-2 mb-sm-0">
                    <CButton
                      v-if="canSubmit"
                      color="primary"
                      :disabled="!isValid || submitting"
                      @click="onSubmit"
                    >
                      <CSpinner v-if="submitting" size="sm" class="me-2" />
                      <i v-else class="fas fa-check-circle me-2"></i>
                      {{ submitting ? 'Saving...' : 'Submit' }}
                    </CButton>
                    <CButton
                      color="warning"
                      variant="outline"
                      class="ms-2"
                      :disabled="isValid"
                      @click="validate"
                    >
                      <i class="fas fa-exclamation-triangle me-2"></i>
                      Validate
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      class="ms-2"
                      :disabled="!isDirty"
                      @click="reset"
                    >
                      <i class="fas fa-undo me-2"></i>
                      Reset
                    </CButton>
                    <CButton
                      color="secondary"
                      class="ms-2"
                      @click="cancel"
                      :disabled="submitting"
                    >
                      <i class="fas fa-times me-2"></i>
                      Cancel
                    </CButton>
                  </div>
                  <div v-if="!isValid" class="text-danger">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    <small>Please fill in all required fields correctly</small>
                  </div>
                </div>
              </CCardFooter>
            </CCard>
          </CTab>

          <!-- Close CTabs -->
        </CTabs>
      </CCard>
      </CCol>
    </CRow>

    <!-- Leader Search Modal (not used in current implementation but kept for compatibility) -->
    <CModal
      :show.sync="leaderSearchPopup"
      size="xl"
    >
      <template #header>
        <h5>
          <i class="fas fa-search me-2"></i>
          Search for Leader
        </h5>
      </template>
      <CRow>
        <CCol sm="12">
          <CCard>
            <CCardBody>
              <CDataTable
                :items="leaderList"
                :fields="leaderFieldList"
                column-filter
                items-per-page-select
                :items-per-page="10"
                hover
                sorter
                pagination
                :loading="loading"
              >
                <template #show_details="{ item, index }">
                  <td class="py-2">
                    <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      @click="onLeaderSelected(item, index)"
                    >
                      <i class="fas fa-check me-1"></i>
                      Select
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <template #footer>
        <CButton color="secondary" @click="leaderSearchPopup = false">
          Close
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ProfileApi from "../../lib/profileApi";
import RoleApi from "../../lib/roleApi";
import SubCompanyApi from "../../lib/subCompanyApi";
import WidgetsUploadDocument from "../widgets/WidgetsUploadDocument";
import Helper from "../../lib/helper";
import {
  required,
  minLength,
  email,
  sameAs,
  helpers,
  numeric,
  decimal,
  maxLength,
} from "vuelidate/lib/validators";

const helper = new Helper();

const leaderList = [];
const leaderFieldList = [
  { key: "fullName", _style: "min-width:200px;" },
  { key: "email", _style: "min-width:200px;" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const packageList = [];
const packageFieldList = [
  { key: "name", _style: "min-width:200px;" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const profilePackageList = [];
const profilePackageFieldList = [
  {
    key: "packageName",
    label: "Name",
  },
  {
    key: "packageApplicationType",
  },
  {
    key: "commisionType",
    label: "Commission Type",
  },
  {
    key: "commisionPercentage",
    label: "Percentage",
  },
  {
    key: "commisionAmount",
    label: "Commision Amount",
  },
  {
    key: "show_remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "User",
  components: {
    WidgetsUploadDocument,
  },
  data: () => {
    return {
      roleApi: new RoleApi(),
      subCompanyApi: new SubCompanyApi(),
      roles: [],
      subCompanies: [],
      selectedRoleId: null,
      isMainLeader: true,
      editCommisionPopup: false,
      searchPackagePopup: false,
      newPackageProfile: {
        packageId: "",
        package: {
          name: "",
        },
        commisionAmount: 0,
      },
      isResetPassword: false,
      plainPassword: "",
      defaultCommisionPercentage: 300,
      isEmailReadOnly: false,
      leaderSearchPopup: false,
      leaderList: leaderList.map((item, id) => {
        return { ...item, id };
      }),
      leaderFieldList,
      selectedLeader: {
        id: null,
        fullName: "",
      },
      packageList: packageList.map((item, id) => {
        return { ...item, id };
      }),
      packageFieldList,
      selectedPackage: {
        id: null,
        name: "",
      },
      profilePackageList: profilePackageList.map((item, index) => {
        return { ...item, index };
      }),
      profilePackageFieldList,
      packageProfileAddPopup: false,
      infoList: [],
      api: new ProfileApi(),
      obj: {
        leaderId: null,
        fullName: "",
        role: "Sales",
        email: "",
        phone: "",
        myKad: "",
        passportNo: "",
        profilePackages: [],
        isEnable: true,
        // Company Information
        subCompanyId: null,
        bankAccount: "",
        tinNo: "",
        companyName: "",
        companyBankAccount: "",
        address: "",
        // Document References
        icCopyDocumentId: null,
        icCopyDocument: null,
        photoDocumentId: null,
        photoDocument: null,
      },
      loading: false,
      submitting: false,
    };
  },
  mounted() {
    // Check view permission
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }

    // If creating new, check CanAdd
    if (!this.$route.params.id && !this.canAdd) {
      this.$router.push('/admins/UserList');
      this.toast('Access Denied', 'You do not have permission to add users', 'danger');
      return;
    }

    console.log("=== USER.VUE MOUNTED ===");
    console.log("Initial obj:", this.obj);
    this.resetObj();
    this.refreshRoles();
    this.refreshSubCompanies();
  },
  watch: {
    'obj.icCopyDocument': function(newVal, oldVal) {
      console.log("=== IC COPY DOCUMENT CHANGED ===");
      console.log("Old value:", oldVal);
      console.log("New value:", newVal);
    },
    'obj.photoDocument': function(newVal, oldVal) {
      console.log("=== PHOTO DOCUMENT CHANGED ===");
      console.log("Old value:", oldVal);
      console.log("New value:", newVal);
    }
  },
  validations: {
    obj: {
      fullName: {
        required,
        minLength: minLength(3),
      },
      email: {
        required,
        email,
      },
      role: {
        required,
      },
      phone: {
        required,
        numeric,
        minLength: minLength(8),
        maxLength: maxLength(11),
      },
    },
  },
  computed: {
    computedProfilePackageList() {
      return this.profilePackageList.map((item) => {
        return {
          ...item,
          packageApplicationType: this.getPackageApplicationType(item),
          packageName: this.getPackageName(item),
          commisionType: this.getCommisionType(item),
          commisionPercentage: this.getCommisionPercentage(item),
        };
      });
    },
    afterCommision() {
      try {
        return (
          this.newPackageProfile.leaderCommisionAmount -
          this.newPackageProfile.commisionAmount
        );
      } catch (error) {}
    },
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
    isValid() {
      return !this.$v.obj.$invalid;
    },
    isDirty() {
      return this.$v.obj.$anyDirty;
    },
    canView() {
      return this.$auth.hasSubModulePermission('USER', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('USER', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('USER', 'CanUpdate');
    },
    isReadOnly() {
      if (this.obj.id) {
        return !this.canUpdate;
      } else {
        return !this.canAdd;
      }
    },
    canSubmit() {
      if (this.obj.id) {
        return this.canUpdate;
      } else {
        return this.canAdd;
      }
    },
  },
  methods: {
    onRoleSelected() {
      const role = this.roles.find((role) => role.value == this.selectedRoleId);
      this.obj.role = role.label;
    },
    refreshRoles() {
      this.loading = true;
      this.roleApi
        .getList()
        .then((response) => {
          for (let i = 0; i < response.result.length; i++) {
            this.roles.push({
              value: response.result[i].id,
              label: response.result[i].name,
            });
          }
          if (this.obj) {
            const role = this.roles.find((role) => role.label == this.obj.role);
            if (role) {
              this.selectedRoleId = role.value;
            }
          }
          this.loading = false;
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
          this.loading = false;
        });
    },
    onIsMainLeaderChanged() {
      if (this.isMainLeader == false) {
        this.selectedLeader = {
          id: null,
          fullName: "",
        };
        this.obj.leaderId = null;
        this.defaultCommisionPercentage = 50;
      } else {
        this.defaultCommisionPercentage = 300;
      }
    },
    getCommisionDescription(item) {
      try {
        if (this.isTopLeader()) {
          return (
            "Top leader commision " +
            item.commisionPercentage +
            "% (RM" +
            item.commisionAmount +
            ")"
          );
        } else {
          return (
            "Subagent commision " +
            item.commisionPercentage +
            "% (RM" +
            item.commisionAmount +
            ")" +
            ". Your commision " +
            this.getLeaderCommisionInPercentage(item) +
            "%, before = RM" +
            this.getLeaderCommision(item) +
            ", after = RM " +
            this.getLeaderAfterCommision(item) +
            ""
          );
        }
      } catch (error) {}
      return "Cannot determine your commision";
    },
    isTopLeader() {
      if (this.obj.leader) return false;
      return true;
    },
    getLeaderCommisionInPercentage(item) {
      try {
        for (var i = 0; this.obj.leader.profilePackages.length; i++) {
          if (item.packageId == this.obj.leader.profilePackages[i].packageId) {
            return this.obj.leader.profilePackages[i].commisionPercentage;
          }
        }
      } catch (error) {}
      return 0;
    },
    getBasedCommision(item) {
      try {
        for (var i = 0; this.packageList.length; i++) {
          if (item.packageId == this.packageList[i].id) {
            return this.packageList[i].commisionAmount;
          }
        }
      } catch (error) {}
      return 0;
    },
    getLeaderCommision(item) {
      try {
        for (var i = 0; this.obj.leader.profilePackages.length; i++) {
          if (item.packageId == this.obj.leader.profilePackages[i].packageId) {
            return this.obj.leader.profilePackages[i].commisionAmount;
          }
        }
      } catch (error) {}
      return 0;
    },
    getLeaderAfterCommision(item) {
      return this.getLeaderCommision(item) - item.commisionAmount;
    },
    onRemovePackage(item) {
      if (this.profilePackageList != null) {
        for (var i = 0; i < this.profilePackageList.length; i++) {
          if (this.profilePackageList[i].packageId === item.packageId) {
            this.profilePackageList.splice(i, 1);
          }
        }
      }
    },
    updateCommisionAmountFor(item) {
      if (item.commisionType == "By Percentage") {
        if (isNaN(item.commisionPercentage) == false) {
          var parentCommision = 0;
          if (this.obj.leaderId) {
            parentCommision = this.getLeaderCommision(item);
          } else {
            parentCommision = this.getBasedCommision(item);
          }
          item.commisionAmount =
            (parentCommision * item.commisionPercentage) / 100;
        }
      }
    },
    updateCommisionAmount() {
      if (this.newPackageProfile.commisionType == "By Percentage") {
        if (isNaN(this.newPackageProfile.commisionPercentage) == false) {
          this.newPackageProfile.commisionAmount =
            (this.newPackageProfile.leaderCommisionAmount *
              this.newPackageProfile.commisionPercentage) /
            100;
        }
      }
    },
    isCommisionPercentage(item) {
      if (item.commisionType == "By Percentage") return true;
      return false;
    },
    getCommisionPercentage(item) {
      if (item.commisionType == "By Percentage")
        return item.commisionPercentage;
      return "N/A";
    },
    getCommisionType(item) {
      if (item.commisionType == null) return "N/A";
      if (item.commisionType == "") return "N/A";
      return item.commisionType;
    },
    getPackageApplicationType(item) {
      if (item.package == null) return "N/A";
      if (item.package.applicationType == null) return "N/A";
      return item.package.applicationType;
    },
    getPackageName(item) {
      if (item.package == null) return "N/A";
      if (item.package.name == null) return "N/A";
      return item.package.name;
    },
    onSubmit() {
      this.submitting = true;

      var profileDto = {
        profile: this.obj,
        isResetPassword: this.isResetPassword,
        plainPassword: this.plainPassword,
      };

      if (this.obj.role == "") this.obj.role = "Sales";

      if (!this.obj.id) {
        this.api
          .createProfileAppUser(profileDto)
          .then((response) => {
            this.toast("Success", "User created successfully", "success");
            this.submitting = false;
            // Navigate to the edit page for the newly created user
            setTimeout(() => {
              this.$router.push({ path: `/admins/User/${response.result.id}` });
            }, 500);
          })
          .catch((error) => {
            const errorMessage = error && error.data
              ? helper.getErrorMessage(error.data)
              : "An error occurred while creating the user";
            this.toast("Error", errorMessage, "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .updateProfileAppUser(profileDto)
          .then((response) => {
            this.toast("Success", "User updated successfully", "success");
            this.submitting = false;
            // Reload the user data to reflect changes
            this.resetObj();
          })
          .catch((error) => {
            const errorMessage = error && error.data
              ? helper.getErrorMessage(error.data)
              : "An error occurred while updating the user";
            this.toast("Error", errorMessage, "danger");
            this.submitting = false;
          });
      }
    },
    onReset(evt) {
      evt.preventDefault();
      this.obj = {};
    },
    toast(header, message, color) {
      const toastId = `toast-${Date.now()}-${Math.random()}`;
      this.infoList.push({
        id: toastId,
        header: header,
        message: message,
        color: color,
      });

      // Auto-remove toast after autohide duration
      setTimeout(() => {
        const index = this.infoList.findIndex(info => info.id === toastId);
        if (index !== -1) {
          this.infoList.splice(index, 1);
        }
      }, 3500); // 3000ms autohide + 500ms buffer
    },
    resetObj() {
      console.log("=== RESET OBJ START ===");
      console.log("Route params ID:", this.$route.params.id);
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .getProfile(this.$route.params.id)
          .then((response) => {
            console.log("Profile loaded from API:", response.result);
            console.log("IC Copy Document:", response.result.icCopyDocument);
            console.log("Photo Document:", response.result.photoDocument);
            console.log("IC Copy Document ID:", response.result.icCopyDocumentId);
            console.log("Photo Document ID:", response.result.photoDocumentId);
            this.obj = response.result;
            this.loading = false;
          })
          .catch(({ data }) => {
            console.error("Error loading profile:", data);
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
          });
      } else {
        this.obj = this.getEmptyObj();
        this.isResetPassword = true;
        this.loading = false;
      }
    },
    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === "");
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      this.resetObj();
      this.submitting = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      return {
        fullName: "",
        email: "",
        phone: "",
        role: "",
        isEnable: true,
      };
    },
    cancel() {
      this.$router.push({ path: "/admins/UserList" });
    },
    onLeaderSelected(item, index) {
      // Placeholder for leader selection functionality
      this.leaderSearchPopup = false;
    },
    // SubCompany methods
    refreshSubCompanies() {
      const self = this;
      self.subCompanyApi.getList().then((response) => {
        self.subCompanies = response.result.map((item) => ({
          value: item.id,
          label: item.name,
        }));
      });
    },
    // IC Copy upload handler
    icCopy_uploaded(data) {
      const self = this;
      console.log("=== IC COPY UPLOAD START ===");
      console.log("Upload data received:", data);
      console.log("Uploaded files:", data.uploadedFiles);

      self.api
        .updateIcCopyDocument(self.obj.id, data.uploadedFiles)
        .then((response) => {
          console.log("IC Copy API response:", response);
          self.toast("Success", "IC Copy uploaded successfully", "success");
          console.log("Calling resetObj to reload profile...");
          self.resetObj();
        })
        .catch((err) => {
          console.error("IC Copy upload error:", err);
          self.toast("Error", helper.getErrorMessage(err), "danger");
        });
    },
    // Photo upload handler
    photo_uploaded(data) {
      const self = this;
      console.log("=== PHOTO UPLOAD START ===");
      console.log("Upload data received:", data);
      console.log("Uploaded files:", data.uploadedFiles);

      self.api
        .updatePhotoDocument(self.obj.id, data.uploadedFiles)
        .then((response) => {
          console.log("Photo API response:", response);
          self.toast("Success", "Photo uploaded successfully", "success");
          console.log("Calling resetObj to reload profile...");
          self.resetObj();
        })
        .catch((err) => {
          console.error("Photo upload error:", err);
          self.toast("Error", helper.getErrorMessage(err), "danger");
        });
    },
    // Delete IC Copy
    deleteIcCopy() {
      if (confirm("Are you sure you want to delete the IC Copy?")) {
        const self = this;
        self.api
          .deleteIcCopyDocument(self.obj.id)
          .then(() => {
            self.toast("Success", "IC Copy deleted successfully", "success");
            self.obj.icCopyDocument = null;
            self.obj.icCopyDocumentId = null;
          })
          .catch((err) => {
            self.toast("Error", helper.getErrorMessage(err), "danger");
          });
      }
    },
    // Delete Photo
    deletePhoto() {
      if (confirm("Are you sure you want to delete the Photo?")) {
        const self = this;
        self.api
          .deletePhotoDocument(self.obj.id)
          .then(() => {
            self.toast("Success", "Photo deleted successfully", "success");
            self.obj.photoDocument = null;
            self.obj.photoDocumentId = null;
          })
          .catch((err) => {
            self.toast("Error", helper.getErrorMessage(err), "danger");
          });
      }
    },
    // Check if document is an image
    isImageDocument(document) {
      if (!document) return false;

      // Check by contentType
      if (document.contentType) {
        const isImage = document.contentType.startsWith('image/');
        console.log(`Document contentType: ${document.contentType}, isImage: ${isImage}`);
        return isImage;
      }

      // Check by extension
      if (document.extension) {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
        const isImage = imageExtensions.includes(document.extension.toLowerCase());
        console.log(`Document extension: ${document.extension}, isImage: ${isImage}`);
        return isImage;
      }

      // Fallback to documentType if it exists
      if (document.documentType) {
        return document.documentType === 'image';
      }

      return false;
    },
    // Get document URL for display
    getDocumentUrl(document) {
      console.log("=== GET DOCUMENT URL ===");
      console.log("Document object:", document);
      console.log("Document path:", document ? document.path : "null");
      console.log("Document driver:", document ? document.driver : "null");

      if (!document) {
        console.log("No document, returning empty string");
        return "";
      }

      // Check if document is stored in database
      if (document.driver === 'db' && document.content) {
        console.log("Document stored in database, generating data URL");
        // Generate data URL from base64 content
        const dataUrl = `data:${document.contentType || 'application/octet-stream'};base64,${document.content}`;
        console.log("Generated data URL (first 100 chars):", dataUrl.substring(0, 100));
        return dataUrl;
      }

      // Check if document has a file path
      if (!document.path) {
        console.log("No path and not database storage, returning empty string");
        return "";
      }

      // Document stored as file, generate file URL
      const apiUrl = window.config.API_URL.replace("/api/", "");
      const fullUrl = helper.removeTrailingSlash(apiUrl) + document.path;
      console.log("Generated file URL:", fullUrl);
      return fullUrl;
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.user-form-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form Card */
.form-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.form-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Form Body */
.form-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

.col-form-label .text-danger {
  font-size: 1.2em;
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Section Header */
.section-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Document Preview Styles */
.document-preview {
  margin-top: 1rem;
}

.preview-card {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
  background: white;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.preview-header strong {
  color: #495057;
  font-size: 0.9rem;
}

.preview-body {
  padding: 0;
  text-align: center;
}

/* Inline iframe for PDFs */
.document-iframe {
  border: 1px solid #ccc;
  border-radius: 0;
  display: block;
}

/* Inline image display */
.document-image-inline {
  max-width: 100%;
  width: 100%;
  height: auto;
  display: block;
  border: none;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .user-form-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }

  .form-footer {
    padding: 1rem;
  }

  .form-footer .d-flex {
    flex-direction: column;
  }

  .form-footer .mb-2 {
    width: 100%;
  }

  .form-footer .btn {
    width: 100%;
    margin-bottom: 0.5rem !important;
    margin-left: 0 !important;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
